<template>
	<query-form v-model:query="query" :loading="loading" @apply="getReportData" @abort="clearQuery">
		<template #display>
			<div class="display-value">
				<div class="label">Date Range:</div>
				<div class="value">{{ formatDate(query.date_range, 'MMM YYYY') }}</div>
			</div>
			<div class="display-value">
				<div class="label">Product:</div>
				<div class="value">{{ displaySelected(query.filters.product, product_options) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Campaign Type:</div>
				<div class="value">{{ displaySelected(query.filters.campaign_type, campaign_type_options) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Vertical:</div>
				<div class="value">{{ displaySelected(query.filters.vertical_id, vertical_options) }}</div>
			</div>
			<div class="display-value">
				<div class="label">Sales Role:</div>
				<div class="value">{{ displaySelected(query.filters.sales_role, sales_role_options) }}</div>
			</div>
		</template>
		<template #utils>
			<p-button
				v-tooltip.top="'Refresh'"
				icon="pi pi-refresh"
				aria-label="Refresh"
				class="mr-2"
				@click="getReportData(query, true)"
			/>
		</template>
		<template #form="form">
			<row>
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<div class="controls gap-10">
								<div class="field">
									<date-range-picker
										v-model="form.query.date_range"
										view="month"
										date-format="M yy"
										:date-options="date_picker_options"
									/>
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.vertical_id"
										:options="$root.appStore.verticalOptions"
										option-label="label"
										option-value="value"
										placeholder="Filter by Vertical"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.campaign_type"
										:options="campaign_type_options"
										option-label="label"
										option-value="value"
										placeholder="Filter by Campaign Type"
									/>
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.sales_role"
										:options="sales_role_options"
										option-label="label"
										option-value="value"
										placeholder="Filter by Sales Role"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column></column>
			</row>
		</template>
	</query-form>
	<div class="card padded">
		<div class="chart">
			<h2>Monthly Performance</h2>
			<line-chart :chart-data="chartData" :custom-options="chart_options" />
		</div>
		<gutter size="20px" />
		<div class="data-table">
			<p-data-table
				:value="report_data"
				ref="performance_table"
				data-key="account_manager_id"
				class="performance-table"
				sort-field="account_manager_name"
				:sort-order="1"
				scrollable
				scrollHeight="calc(100vh - 420px)"
			>
				<template #empty>
					<div class="dim">No Data</div>
				</template>
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<p-column header="Account Manager" sortable field="account_manager_name" frozen>
					<template #body="row">
						{{ row.data.account_manager_name.trim() || 'Unknown User' }}<br />
						<div class="sub-value">{{ title(row.data.sales_role) || 'Unknown Role' }}</div>
					</template>
				</p-column>
				<p-column
					v-for="(month, index) in months"
					:header="monthLabels[index]"
					:key="month"
					sortable
					:field="`spend.${month}`"
					class="column-align-right"
				>
					<template #body="row">
						{{ currency(row.data.spend[month] || 0) }}<br />
						<div
							class="sub-value"
							v-html="trend(row.data.spend[month], row.data.spend[index === 0 ? previousMonth : months[index - 1]])"
						></div>
					</template>
				</p-column>
			</p-data-table>
		</div>
	</div>
</template>

<script>
import dateRangePicker from '@/components/forms/DateRangePicker.vue';
import lineChart from '@/components/charts/Line.vue';
import queryForm from '@/components/widgets/QueryForm.vue';
import { currency, displaySelected, formatDate, title, trend } from '@/lib/Filters';
import dayjs from 'dayjs';
import pMultiSelect from 'primevue/multiselect';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { reportAMPerformance } from '@GQL';

dayjs.extend(isSameOrBefore);

export default {
	name: 'AMPerformanceTable',
	components: {
		dateRangePicker,
		lineChart,
		pMultiSelect,
		queryForm,
	},
	props: {
		metrics: {
			type: Array,
			default() {
				return [];
			},
		},
	},
	data() {
		return {
			loading: false,
			query: {
				date_range: [dayjs().subtract(5, 'months').startOf('month').toDate(), dayjs().endOf('month').toDate()],
				filters: {
					product: [],
					campaign_type: [],
					vertical_id: [],
					sales_role: [],
				},
			},
			date_range: [dayjs().subtract(5, 'months').startOf('month').toDate(), dayjs().endOf('month').toDate()],
			product_options: [
				{ label: 'Data', value: 'data' },
				{ label: 'Calls', value: 'call' },
				{ label: 'Live Transfers', value: 'live_transfer' },
				{ label: 'Ads', value: 'ad' },
			],
			campaign_type_options: [
				{ label: 'Exclusive Data', value: 'exclusive_data' },
				{ label: 'Shared Data', value: 'shared_data' },
				{ label: 'Premium Listing', value: 'premium_listing' },
				{ label: 'Call', value: 'call' },
				{ label: 'Live Transfer', value: 'live_transfer' },
			],
			sales_role_options: [
				{ label: 'Account Manager', value: 'account_manager' },
				{ label: 'Sr. Account Executive', value: 'sr_account_executive' },
				{ label: 'Account Executive', value: 'account_executive' },
				{ label: 'Sales Associate', value: 'sales_associate' },
				{ label: 'Management', value: 'management' },
				{ label: 'Other', value: 'other' },
			],
			vertical_options: [],
			report_data: [],
			chart_options: {
				responsive: true,
				plugins: {
					tooltip: {
						callbacks: {
							label: function (tooltip) {
								return ` ${tooltip.dataset.label}: ${currency(tooltip.parsed.y || 0)}`;
							},
						},
					},
					legend: {
						position: 'bottom',
					},
				},
				scales: {
					y: {
						type: 'linear',
						display: true,
						position: 'left',
						min: 0,
						ticks: {
							callback: function (value) {
								return currency(value);
							},
						},
					},
				},
				layout: {
					padding: 30,
				},
			},
			date_picker_options: [
				{
					icon: 'calendar-today',
					label: 'This Calendar Year',
					value: [dayjs().startOf('year').toDate(), dayjs().endOf('year').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last Calendar Year',
					value: [
						dayjs().startOf('year').subtract(1, 'year').toDate(),
						dayjs().subtract(1, 'year').endOf('year').toDate(),
					],
				},
				{
					icon: 'calendar-today',
					label: 'Last 6 Months',
					value: [dayjs().startOf('month').subtract(5, 'months').toDate(), dayjs().endOf('month').toDate()],
				},
				{
					icon: 'calendar-today',
					label: 'Last 12 Months',
					value: [dayjs().startOf('month').subtract(11, 'months').toDate(), dayjs().endOf('month').toDate()],
				},
			],
		};
	},
	computed: {
		dateRange() {
			return [dayjs(this.date_range[0]).format('YYYY-MM'), dayjs(this.date_range[1]).format('YYYY-MM')];
		},
		months() {
			const dates = [];
			let month = dayjs(this.dateRange[0]);
			while (month.isSameOrBefore(this.dateRange[1])) {
				dates.push(month.format('YYYY-MM'));
				month = month.add(1, 'month');
			}
			return dates;
		},
		monthLabels() {
			return this.months.map((month) => {
				return dayjs(month).format("MMM 'YY");
			});
		},
		previousMonth() {
			return dayjs(this.date_range[0]).subtract(1, 'month').format('YYYY-MM');
		},
		chartData() {
			return {
				labels: this.monthLabels,
				datasets: this.report_data.map((row) => {
					return {
						label: row.account_manager_name,
						data: this.months.map((month) => row.spend[month]),
						fill: true,
						pointStyle: 'circle',
						pointRadius: 5,
						pointBackgroundColor: 'white',
						pointBorderWidth: 3,
					};
				}),
			};
		},
	},
	async mounted() {
		if (
			this.$root.sessionStore.user?.settings?.team === 'sales' &&
			this.$root.sessionStore.user?.settings?.sales_role !== 'management'
		) {
			this.query.filters.sales_role = [this.$root.sessionStore.user?.settings?.sales_role];
		}
		await this.getReportData(this.query);
		this.vertical_options = this.$root.appStore.verticalOptions;
	},
	methods: {
		currency,
		displaySelected,
		formatDate,
		title,
		trend,
		async getReportData(query, refresh) {
			try {
				this.query = query;
				this.loading = true;

				const params = { ...query };

				// Get an extra month for growth velocity
				const date_range_w_extra_month = [
					dayjs(query.date_range[0]).subtract(1, 'month').format('YYYY-MM'),
					dayjs(query.date_range[1]).format('YYYY-MM'),
				];
				params.date_range = date_range_w_extra_month;
				this.report_data = await reportAMPerformance(params, refresh);
				this.date_range = query.date_range;
			} finally {
				this.loading = false;
			}
		},
		clearQuery() {
			this.loading = false;
		},
	},
};
</script>
